// DispatchComposer — phone UI to create a remote dispatch (a task that runs
// on the user's Mac when the app is open).

import { useEffect, useState } from 'react';
import { createDispatch, getRoots } from '../../lib/remoteDispatchClient.js';
import TouchButton from '../ui/TouchButton.jsx';
import BrainPicker from './BrainPicker.jsx';
import FolderPicker from './FolderPicker.jsx';

export default function DispatchComposer({ onCreated }) {
  const [prompt, setPrompt] = useState('');
  const [folder, setFolder] = useState('');
  const [brain, setBrain] = useState(null);
  const [autonomy, setAutonomy] = useState('ask');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [roots, setRoots] = useState([]);

  useEffect(() => {
    getRoots()
      .then(setRoots)
      .catch(() => setRoots([]));
  }, []);

  const canSubmit = prompt.trim().length > 0 && !busy;

  async function submit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const root = folder.trim();
      const context = {};
      if (root) context.root = root;
      if (brain) context.brain = brain;
      const result = await createDispatch({
        prompt: prompt.trim(),
        autonomy,
        context: Object.keys(context).length ? context : undefined,
      });
      setPrompt('');
      onCreated?.(result);
    } catch (err) {
      setError(err?.message || 'Falha ao disparar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <label htmlFor="dispatch-prompt" className="text-sm font-medium text-apolo-textSecondary">
        Tarefa para o Apolo rodar na sua máquina
      </label>
      <textarea
        id="dispatch-prompt"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={4}
        placeholder="Ex: rode os testes e me diga o que quebrou"
        className="w-full resize-y rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-3 text-sm text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50"
      />
      <FolderPicker roots={roots} value={folder} onChange={setFolder} />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-apolo-textTertiary">Cérebro</span>
        <BrainPicker value={brain} onChange={setBrain} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-apolo-textTertiary">
          <input
            type="checkbox"
            checked={autonomy === 'auto'}
            onChange={(e) => setAutonomy(e.target.checked ? 'auto' : 'ask')}
          />
          Autônomo (não pedir aprovação)
        </label>
        <TouchButton type="submit" variant="primary" disabled={!canSubmit}>
          {busy ? 'Disparando…' : 'Disparar'}
        </TouchButton>
      </div>
      {error && <p className="text-xs text-apolo-error">{error}</p>}
    </form>
  );
}
